import { SearchContext } from "../../contexts/SearchContext";
import { translate } from "../../i18n/translate";
import { DateRange } from "../../models/DateRange";
import { DateRangePicker } from "../../components/DateRangePicker";
import { DateLocalizationHelper } from "../../common/DateLocalizationHelper";
import languageObserver from "../../common/LanguageChangeObserver";


type DateRangeCallback = (dateRange: DateRange) => void;

export class DateRangeSearchFilter {
    private readonly element: HTMLElement;
    private readonly searchContext: SearchContext;
    private readonly onFilterApplied: DateRangeCallback;
    private options: {[key: string]: string};
    private selectedStartDate: Date | null = null;
    private selectedEndDate: Date | null = null;
    private dateRangePicker: DateRangePicker | null = null;
    private readonly dateHelper: DateLocalizationHelper;
    private languageUnsubscribe: (() => void) | null = null;

    constructor(element : HTMLElement, searchContext: SearchContext, onFilterApplied: DateRangeCallback) {
        this.element = element;
        this.searchContext = searchContext;
        this.dateHelper = DateLocalizationHelper.getInstance();
        this.options = {
            '-7d': 'Hero.Date.LastWeek',
            '-1m': 'Hero.Date.LastMonth',
            '-3m': 'Hero.Date.Last3Months',
            '-1y': 'Hero.Date.Last12Months'
        };
        this.onFilterApplied = onFilterApplied;
        this.element.classList.add('horizontal-filter', 'horizontal-filter--daterange');
        
        // Initialize from existing context
        this.initializeFromContext();
        
        this.render();
        
        // Update display to show current state
        this.updateDisplay();
        
        // Subscribe to language changes
        this.languageUnsubscribe = languageObserver.subscribe((locale: string) => {
            this.updateLocale(locale);
        });
    }



    private render() : void {
        const currentText = this.getCurrentFilterText();
        
        this.element.innerHTML = `
            <button type="button" id="date-range-search-filter-button" class="horizontal-filter__dropdown-btn dropdown-toggle" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                <span>${currentText}</span>
            </button>
            <button type="button" class="horizontal-filter__clear-btn date-range-clear-btn">
                <svg class="icon" viewBox="0 0 32 32">
                    <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="/images/icons-sprite.svg#cross-32"></use>
                </svg>
                <span class="screen-reader-text">${translate('Hero.ClearFilter')}</span>
            </button>
            <div class="horizontal-filter__menu dropdown-menu">
                <ul class="horizontal-filter__options">
                    <li class="horizontal-filter__item">
                        <button class="date-range-search-filter-option" data-date="-7d">${translate('Hero.Date.LastWeek')}</button>
                    </li>

                    <li class="horizontal-filter__item">
                        <button class="date-range-search-filter-option" data-date="-1m">${translate('Hero.Date.LastMonth')}</button>
                    </li>

                    <li class="horizontal-filter__item">
                        <button class="date-range-search-filter-option" data-date="-3m">${translate('Hero.Date.Last3Months')}</button>
                    </li>

                    <li class="horizontal-filter__item">
                        <button class="date-range-search-filter-option" data-date="-1y">${translate('Hero.Date.Last12Months')}</button>
                    </li>

                    <li class="horizontal-filter__extend-btn">
                        <button class="date-range-search-filter-option" id="date-range-filter">${translate('Hero.Date.Custom')}</button>
                    </li>
                </ul>

                <div class="horizontal-filter__extend-container"></div>
            </div>
        `;

        this.initializeEventListeners();
    }

    private getCurrentFilterText(): string {
        if (this.selectedStartDate && this.selectedEndDate) {
            return this.dateHelper.formatDateRange(this.selectedStartDate, this.selectedEndDate);
        } else if (this.selectedStartDate) {
            return this.dateHelper.formatDate(this.selectedStartDate);
        } else if (this.searchContext.selectedDateOffset && this.searchContext.selectedDateOffset !== '') {
            if (this.searchContext.selectedDateOffset.startsWith('custom:')) {
                return this.searchContext.selectedDateOffset.replace('custom:', '');
            } else {
                return translate(this.options[this.searchContext.selectedDateOffset]) || translate('Hero.Date');
            }
        } else {
            return translate('Hero.Date');
        }
    }

    private initializeEventListeners(): void {
        const clearButton = this.element.querySelector('.date-range-clear-btn');
        const dropdownMenu = this.element.querySelectorAll('.horizontal-filter__menu');
        const applyFilterButton = this.element.querySelectorAll('.date-range-search-filter-option')
        
        if (clearButton) {
            clearButton.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                this.clearFilter();
            }, { capture: true });
        }
        
        dropdownMenu?.forEach(x => x.addEventListener('click', (event) => event.stopPropagation()));
        applyFilterButton?.forEach((x : Element) => {
            x.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                this.applyFilter(x, event);
            });
        });
    }
    
    public clearFilter() {
        // Clear the selected date offset in search context
        this.searchContext.selectedDateOffset = '';
        
        // Clear stored dates
        this.selectedStartDate = null;
        this.selectedEndDate = null;
        
        // Remove active state
        this.element.classList.remove('horizontal-filter--active');
        
        // Hide custom date range if it's open
        this.hideCustomDateRange();
        
        // Update display to show cleared state
        this.updateDisplay();
        
        // Apply empty date range filter
        const emptyDateRange: DateRange = { dateFrom: '', dateTo: null };
        this.onFilterApplied(emptyDateRange);
    }

    private applyFilter(button: Element, event?: Event) {
        const filterButton = document.getElementById('date-range-search-filter-button') as HTMLButtonElement;
        const dateOffset = button.getAttribute('data-date');
        
        // Check if this is the custom date range button
        if (button.id === 'date-range-filter') {
            // Prevent the dropdown from closing by stopping event propagation
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }
            this.showCustomDateRange();
            return;
        }
        
        if (dateOffset) {
            this.searchContext.selectedDateOffset = dateOffset;
            
            // Clear custom date selection when using predefined ranges
            this.selectedStartDate = null;
            this.selectedEndDate = null;
            
            const dateFrom = this.getFormattedDate(dateOffset);
            const dateRange : DateRange = {dateFrom: dateFrom, dateTo: null};
            
            // Update display
            this.updateDisplay();
            
            // Apply the filter
            this.onFilterApplied(dateRange);
            
            // Close the dropdown menu
            const dropdownMenu = this.element.querySelector('.horizontal-filter__menu') as HTMLElement;
            const dropdownButton = this.element.querySelector('.horizontal-filter__dropdown-btn') as HTMLElement;
            
            if (dropdownMenu) {
                dropdownMenu.classList.remove('show');
            }
            if (dropdownButton) {
                dropdownButton.setAttribute('aria-expanded', 'false');
            }
        }
        
    }

    private showCustomDateRange() {
        const extendContainer = this.element.querySelector('.horizontal-filter__extend-container') as HTMLElement;
        const dropdownMenu = this.element.querySelector('.horizontal-filter__menu') as HTMLElement;
        const dropdownButton = this.element.querySelector('.horizontal-filter__dropdown-btn') as HTMLElement;
        
        if (!extendContainer) return;
        
        // Toggle visibility
        if (this.dateRangePicker) {
            this.dateRangePicker.destroy();
            this.dateRangePicker = null;
            if (dropdownMenu) {
                dropdownMenu.classList.remove('show');
            }
            if (dropdownButton) {
                dropdownButton.setAttribute('aria-expanded', 'false');
            }
            return;
        }
        
        // Force dropdown to stay open
        if (dropdownMenu) {
            dropdownMenu.classList.add('show');
            dropdownMenu.style.setProperty('display', 'block', 'important');
            dropdownMenu.style.setProperty('visibility', 'visible', 'important');
            dropdownMenu.classList.add('horizontal-filter__menu--extended');
        }
        if (dropdownButton) {
            dropdownButton.setAttribute('aria-expanded', 'true');
        }
        
        // Force the extend container to be visible
        extendContainer.style.setProperty('display', 'block', 'important');
        extendContainer.style.setProperty('visibility', 'visible', 'important');
        extendContainer.style.setProperty('opacity', '1', 'important');
        
        // Create and initialize the DateRangePicker
        this.dateRangePicker = new DateRangePicker(extendContainer, {
            selectedStartDate: this.selectedStartDate,
            selectedEndDate: this.selectedEndDate,
            onApply: (startDate: Date | null, endDate: Date | null) => {
                this.applyDateSelection(startDate, endDate);
            },
            onCancel: () => {
                this.hideCustomDateRange();
            }
        });
        
        this.dateRangePicker.render();
    }

    private hideCustomDateRange() {
        const extendContainer = this.element.querySelector('.horizontal-filter__extend-container') as HTMLElement;
        const dropdownMenu = this.element.querySelector('.horizontal-filter__menu') as HTMLElement;
        const dropdownButton = this.element.querySelector('.horizontal-filter__dropdown-btn') as HTMLElement;
        
        if (this.dateRangePicker) {
            this.dateRangePicker.destroy();
            this.dateRangePicker = null;
        }
        
        // Close the dropdown
        if (dropdownMenu) {
            dropdownMenu.classList.remove('show');
            dropdownMenu.classList.remove('horizontal-filter__menu--extended');
        }
        if (dropdownButton) {
            dropdownButton.setAttribute('aria-expanded', 'false');
        }
    }

    private getFormattedDate(offset: string): string {
        const now = new Date();
        const date = new Date(now);
    
        const offsetPattern = /^([-+]\d+)([ymdhMs])$/;
        const match = offset.match(offsetPattern);
    
        if (match) {
            const value = parseInt(match[1], 10);
            const unit = match[2];
    
            switch (unit) {
                case 'y':
                    date.setFullYear(date.getFullYear() + value);
                    break;
                case 'm':
                    date.setMonth(date.getMonth() + value);
                    break;
                case 'd':
                    date.setDate(date.getDate() + value);
                    break;
                case 'h':
                    date.setHours(date.getHours() + value);
                    break;
                case 'M':
                    date.setMinutes(date.getMinutes() + value);
                    break;
                case 's':
                    date.setSeconds(date.getSeconds() + value);
                    break;
                default:
                    throw new Error('Unsupported time unit');
            }
        } else {
            throw new Error('Invalid offset format');
        }
    
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        const milliseconds = String(date.getMilliseconds()).padStart(3, '0');
    
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
    }

    private formatDate(date: Date): string {
        return this.dateHelper.formatDate(date);
    }

    private applyDateSelection(startDate: Date | null, endDate: Date | null): void {
        if (!startDate) {
            return;
        }
        
        // Save the selected dates to instance variables
        this.selectedStartDate = startDate;
        this.selectedEndDate = endDate;
        
        const formattedDateRange = endDate 
            ? this.dateHelper.formatDateRange(startDate, endDate)
            : this.dateHelper.formatDate(startDate);
        
        // Update search context with the new date filter - use consistent format
        this.searchContext.selectedDateOffset = `custom:${formattedDateRange}`;
        
        // Apply the date range
        const dateRange: DateRange = {
            dateFrom: startDate.toISOString().split('T')[0],
            dateTo: endDate ? endDate.toISOString().split('T')[0] : null
        };
        
        // Hide the custom date range picker first
        this.hideCustomDateRange();
        
        // Update the display
        this.updateDisplay();
        
        // Apply the filter
        this.onFilterApplied(dateRange);
        
        // Update display again after filter is applied in case the callback affects the component state
        this.updateDisplay();
        
        // Close the main dropdown menu more reliably
        const dropdownButton = this.element.querySelector('.horizontal-filter__dropdown-btn') as HTMLElement;
        const dropdownMenu = this.element.querySelector('.horizontal-filter__menu') as HTMLElement;
        
        // Try multiple approaches to close the dropdown
        if (dropdownButton && (window as any).bootstrap) {
            // Method 1: Try getting existing instance
            let dropdown = (window as any).bootstrap.Dropdown.getInstance(dropdownButton);
            
            // Method 2: If no instance, try creating one and then hiding
            if (!dropdown) {
                try {
                    dropdown = new (window as any).bootstrap.Dropdown(dropdownButton);
                } catch (e) {
                    console.warn('Could not create Bootstrap dropdown instance:', e);
                }
            }
            
            // Hide using Bootstrap API
            if (dropdown) {
                dropdown.hide();
            }
        }
        
        // Fallback: Always apply manual class manipulation as backup
        if (dropdownMenu) {
            dropdownMenu.classList.remove('show');
            dropdownMenu.style.removeProperty('display');
            dropdownMenu.style.removeProperty('visibility');
        }
        if (dropdownButton) {
            dropdownButton.setAttribute('aria-expanded', 'false');
            dropdownButton.classList.remove('show');
        }
        
        // Extra fallback: Force close after a small delay
        setTimeout(() => {
            if (dropdownMenu) {
                dropdownMenu.classList.remove('show');
                dropdownMenu.style.display = '';
            }
            if (dropdownButton) {
                dropdownButton.setAttribute('aria-expanded', 'false');
            }
            
            // Ensure display is updated after all dropdown closing operations
            this.updateDisplay();
        }, 100);
    }

    public updateDisplay(): void {
        const currentText = this.getCurrentFilterText();
        const filterButton = this.element.querySelector('.horizontal-filter__dropdown-btn span') as HTMLElement;
        if (filterButton) {
            filterButton.textContent = currentText;
        }
        
        // Update active state
        if (this.selectedStartDate || this.searchContext.selectedDateOffset) {
            this.element.classList.add('horizontal-filter--active');
        } else {
            this.element.classList.remove('horizontal-filter--active');
        }
    }

    private initializeFromContext(): void {
        if (this.searchContext.selectedDateOffset && this.searchContext.selectedDateOffset.startsWith('custom:')) {
            const dateRange = this.searchContext.selectedDateOffset.replace('custom:', '');
            
            // Try splitting by different separators
            let dates: string[] = [];
            if (dateRange.includes(' - ')) {
                dates = dateRange.split(' - ');
            } else if (dateRange.includes('-')) {
                // Find the last dash that separates dates (not part of date format)
                const parts = dateRange.split('-');
                if (parts.length >= 2) {
                    // For format like "8/12/2025-8/16/2025"
                    const middleIndex = Math.floor(parts.length / 2);
                    const startPart = parts.slice(0, middleIndex).join('-');
                    const endPart = parts.slice(middleIndex).join('-');
                    
                    // Try a different approach - look for date pattern
                    const datePattern = /(\d{1,2}\/\d{1,2}\/\d{4})/g;
                    const foundDates = dateRange.match(datePattern);
                    if (foundDates && foundDates.length === 2) {
                        dates = foundDates;
                    }
                }
            }
            
            if (dates.length === 2) {
                try {
                    // Use the helper's parseDate method for reliable parsing
                    const startDate = this.dateHelper.parseDate(dates[0]);
                    const endDate = this.dateHelper.parseDate(dates[1]);
                    
                    if (startDate && endDate) {
                        this.selectedStartDate = startDate;
                        this.selectedEndDate = endDate;
                        this.updateDisplay();
                    } else {
                        console.warn('Could not parse dates from context:', dates);
                        this.selectedStartDate = null;
                        this.selectedEndDate = null;
                    }
                } catch (e) {
                    console.error('Error parsing dates from context:', e);
                    this.selectedStartDate = null;
                    this.selectedEndDate = null;
                }
            }
        }
    }

    public updateLocale(locale?: string): void {
        if (locale) {
            this.dateHelper.setLocale(locale);
        }
        
        // Update the date range picker if it's currently open
        if (this.dateRangePicker) {
            this.dateRangePicker.updateLocale();
        }
        
        // Update the display text
        this.updateDisplay();
    }

    public destroy(): void {
        // Clean up the date range picker
        if (this.dateRangePicker) {
            this.dateRangePicker.destroy();
            this.dateRangePicker = null;
        }
        
        // Unsubscribe from language changes
        if (this.languageUnsubscribe) {
            this.languageUnsubscribe();
            this.languageUnsubscribe = null;
        }
    }

    // Public getter for element
    public get htmlElement(): HTMLElement {
        return this.element;
    }
}